import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Check, X, Filter, Printer } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

export default function ChangeRequestsPage() {
  const [requests, setRequests] = useState([]);
  const [batchRequests, setBatchRequests] = useState([]);
  const [products, setProducts] = useState([]);
  const [stores, setStores] = useState([]);
  const [filter, setFilter] = useState("pending"); // pending, approved, rejected, auto_applied, all
  const [tab, setTab] = useState("batch"); // 'batch' | 'legacy'
  const [loading, setLoading] = useState(true);
  const [reviewTarget, setReviewTarget] = useState(null);
  const [reviewBatchTarget, setReviewBatchTarget] = useState(null);
  const [reviewAction, setReviewAction] = useState(null); // approve, reject
  const [reviewComment, setReviewComment] = useState("");

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const canApprove = user.role === "admin";

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [reqRes, batchRes, prodRes, storesRes] = await Promise.all([
        axios.get(`${API}/orders/change-requests`, getAuthHeaders()),
        axios.get(`${API}/orders/change-requests/batch`, getAuthHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API}/products`, getAuthHeaders()),
        axios.get(`${API}/stores`, getAuthHeaders()),
      ]);
      setRequests(reqRes.data);
      setBatchRequests(Array.isArray(batchRes.data) ? batchRes.data : []);
      setProducts(prodRes.data);
      setStores(storesRes.data);
    } catch (e) {
      toast.error("データの取得に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  const productsMap = useMemo(() => {
    const m = {};
    products.forEach((p) => (m[p.id] = p));
    return m;
  }, [products]);

  const storesMap = useMemo(() => {
    const m = {};
    stores.forEach((s) => (m[s.id] = s));
    return m;
  }, [stores]);

  const filtered = requests.filter((r) => filter === "all" || r.status === filter);
  // 申請中フィルタ: pending に加え、まだ未処理アイテムが残る partially_approved も表示
  const filteredBatch = batchRequests.filter((r) => {
    if (filter === "all") return true;
    if (filter === "pending") {
      if (r.status === "pending") return true;
      if (r.status === "partially_approved") {
        return (r.items || []).some((it) => (it.status || "pending") === "pending");
      }
      return false;
    }
    return r.status === filter;
  });

  const openReview = (req, action) => {
    setReviewTarget(req);
    setReviewBatchTarget(null);
    setReviewAction(action);
    setReviewComment("");
  };

  const openBatchReview = (batch, action) => {
    setReviewBatchTarget(batch);
    setReviewTarget(null);
    setReviewAction(action);
    setReviewComment("");
  };

  const submitReview = async () => {
    try {
      if (reviewBatchTarget) {
        if (reviewBatchTarget.itemIndex != null) {
          await axios.put(
            `${API}/orders/change-requests/batch/${reviewBatchTarget.id}/items/${reviewBatchTarget.itemIndex}/${reviewAction}`,
            { comment: reviewComment },
            getAuthHeaders()
          );
        } else {
          await axios.put(
            `${API}/orders/change-requests/batch/${reviewBatchTarget.id}/${reviewAction}`,
            { comment: reviewComment },
            getAuthHeaders()
          );
        }
      } else if (reviewTarget) {
        await axios.put(
          `${API}/orders/change-requests/${reviewTarget.id}/${reviewAction}`,
          { comment: reviewComment },
          getAuthHeaders()
        );
      } else {
        return;
      }
      toast.success(reviewAction === "approve" ? "承認しました" : "却下しました");
      setReviewTarget(null);
      setReviewBatchTarget(null);
      setReviewAction(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "処理に失敗しました");
    }
  };

  const statusBadge = (status) => {
    const map = {
      pending: "bg-amber-100 text-amber-700 border-amber-200",
      approved: "bg-emerald-100 text-emerald-700 border-emerald-200",
      rejected: "bg-red-100 text-red-700 border-red-200",
      partially_approved: "bg-blue-100 text-blue-700 border-blue-200",
      auto_applied: "bg-sky-100 text-sky-700 border-sky-200",
    };
    const label = {
      pending: "申請中",
      approved: "承認済",
      rejected: "却下",
      partially_approved: "一部承認",
      auto_applied: "自動反映",
    };
    return (
      <span className={`inline-block px-2 py-0.5 text-xs rounded-sm border ${map[status] || map.pending}`}>
        {label[status] || status}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div>
          <h2 className="text-xl font-semibold font-chivo tracking-tight text-slate-800">
            変更申請確認ボックス
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            店舗から送信された変更依頼を確認・承認/却下できます。
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex rounded-sm border border-slate-300 overflow-hidden">
            <button
              data-testid="cr-tab-batch"
              onClick={() => setTab("batch")}
              className={`h-9 px-3 text-sm ${
                tab === "batch" ? "bg-slate-900 text-white" : "bg-white text-slate-700"
              }`}
            >
              一括変更申請
            </button>
            <button
              data-testid="cr-tab-legacy"
              onClick={() => setTab("legacy")}
              className={`h-9 px-3 text-sm ${
                tab === "legacy" ? "bg-slate-900 text-white" : "bg-white text-slate-700"
              }`}
            >
              旧通知ログ
            </button>
          </div>
          <Filter className="h-4 w-4 text-slate-500" />
          <select
            data-testid="cr-filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="h-9 px-3 rounded-sm border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="pending">申請中</option>
            <option value="approved">承認済</option>
            <option value="partially_approved">一部承認</option>
            <option value="rejected">却下</option>
            {tab === "legacy" && <option value="auto_applied">自動反映（通知）</option>}
            <option value="all">すべて</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-500">読み込み中...</div>
      ) : tab === "batch" ? (
        filteredBatch.length === 0 ? (
          <div className="text-center py-12 text-slate-500 border border-slate-200 rounded-sm bg-white">
            該当する変更申請はありません
          </div>
        ) : (
          <div className="space-y-3">
            {filteredBatch.map((b) => {
              const store = storesMap[b.store_id] || {};
              const counts = {
                modify: (b.items || []).filter((i) => i.action === "modify").length,
                add: (b.items || []).filter((i) => i.action === "add").length,
                delete: (b.items || []).filter((i) => i.action === "delete").length,
              };
              return (
                <div
                  key={b.id}
                  data-testid={`batch-card-${b.id}`}
                  className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden"
                >
                  <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3 flex-wrap text-sm">
                      <span className="font-semibold text-slate-700">{store.name || "-"}</span>
                      <span className="text-slate-500">配送日:</span>
                      <span className="font-mono text-slate-700">{b.delivery_date}</span>
                      <span className="text-slate-500">申請日時:</span>
                      <span className="text-slate-600 text-xs">
                        {new Date(b.created_at).toLocaleString("ja-JP")}
                      </span>
                      {statusBadge(b.status)}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs text-slate-600">
                        変更{counts.modify} / 追加{counts.add} / 削除{counts.delete}
                      </span>
                      {canApprove && (b.status === "pending" || b.status === "partially_approved") && (
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            onClick={() => openBatchReview(b, "approve")}
                            data-testid={`batch-approve-${b.id}`}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm h-8"
                          >
                            <Check className="h-3.5 w-3.5 mr-1" />
                            一括承認
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => openBatchReview(b, "reject")}
                            data-testid={`batch-reject-${b.id}`}
                            variant="outline"
                            className="text-red-600 border-red-300 hover:bg-red-50 rounded-sm h-8"
                          >
                            <X className="h-3.5 w-3.5 mr-1" />
                            一括却下
                          </Button>
                        </div>
                      )}
                      {b.status !== "pending" && (
                        <Link
                          to={`/admin/print/picking-list?date=${b.delivery_date}`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-sm bg-slate-900 text-white hover:bg-slate-800"
                        >
                          <Printer className="h-3 w-3" />
                          印刷
                        </Link>
                      )}
                    </div>
                  </div>
                  <table className="w-full">
                    <thead className="bg-slate-50/50 border-b border-slate-100">
                      <tr>
                        <th className="h-8 px-4 text-left text-[10px] font-medium text-slate-500 uppercase w-20">種別</th>
                        <th className="h-8 px-4 text-left text-[10px] font-medium text-slate-500 uppercase">商品</th>
                        <th className="h-8 px-4 text-right text-[10px] font-medium text-slate-500 uppercase">変更内容</th>
                        <th className="h-8 px-4 text-center text-[10px] font-medium text-slate-500 uppercase w-44">処理</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(b.items || []).map((it, i) => {
                        const p = productsMap[it.product_id];
                        const label = it.action === "modify" ? "変更" : it.action === "add" ? (it.is_new_line ? "締切後発注" : "追加") : "削除";
                        const color =
                          it.action === "modify"
                            ? "bg-amber-100 text-amber-700 border-amber-200"
                            : it.action === "add"
                            ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                            : "bg-red-100 text-red-700 border-red-200";
                        const itemStatus = it.status || "pending";
                        const itemStatusLabel =
                          itemStatus === "approved" ? "承認済" : itemStatus === "rejected" ? "却下" : "未処理";
                        const itemStatusColor =
                          itemStatus === "approved"
                            ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                            : itemStatus === "rejected"
                            ? "bg-red-100 text-red-700 border-red-200"
                            : "bg-slate-100 text-slate-600 border-slate-200";
                        return (
                          <tr key={i} className="border-b border-slate-100 last:border-b-0">
                            <td className="p-2 px-4">
                              <span className={`text-[10px] px-1.5 py-0.5 rounded-sm border ${color}`}>
                                {label}
                              </span>
                            </td>
                            <td className="p-2 px-4 text-sm">
                              <div className="font-medium">{p?.name || it.product_name || "-"}</div>
                              <div className="text-xs text-slate-500">{p?.code || (it.other_order_id ? "リストなし/季節" : "")}</div>
                            </td>
                            <td className="p-2 px-4 text-sm font-mono text-right">
                              {it.action === "modify"
                                ? `${(it.old_quantity || 0).toFixed(2)} ${it.old_unit || ""} → ${(it.new_quantity || 0).toFixed(2)} ${it.new_unit || ""}`
                                : it.action === "add"
                                ? `+ ${(it.new_quantity || 0).toFixed(2)} ${it.new_unit || ""}`
                                : `- ${(it.old_quantity || 0).toFixed(2)} ${it.old_unit || ""}`}
                            </td>
                            <td className="p-2 px-4 text-center">
                              {canApprove && itemStatus === "pending" ? (
                                <div className="inline-flex gap-1">
                                  <Button
                                    size="sm"
                                    onClick={() => openBatchReview({ ...b, itemIndex: i }, "approve")}
                                    data-testid={`item-approve-${b.id}-${i}`}
                                    className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm h-7 text-xs px-2"
                                  >
                                    <Check className="h-3 w-3 mr-1" />
                                    承認
                                  </Button>
                                  <Button
                                    size="sm"
                                    onClick={() => openBatchReview({ ...b, itemIndex: i }, "reject")}
                                    data-testid={`item-reject-${b.id}-${i}`}
                                    variant="outline"
                                    className="text-red-600 border-red-300 hover:bg-red-50 rounded-sm h-7 text-xs px-2"
                                  >
                                    <X className="h-3 w-3 mr-1" />
                                    却下
                                  </Button>
                                </div>
                              ) : (
                                <span className={`text-[10px] px-1.5 py-0.5 rounded-sm border ${itemStatusColor}`}>
                                  {itemStatusLabel}
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {b.reason && (
                    <div className="px-4 py-2 text-xs text-slate-600 bg-slate-50 border-t border-slate-100">
                      理由: {b.reason}
                    </div>
                  )}
                  {b.status !== "pending" && (
                    <div className="px-4 py-2 text-xs text-slate-500 bg-slate-50 border-t border-slate-100">
                      {b.reviewed_by_name} {b.review_comment ? `(${b.review_comment})` : ""}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-slate-500 border border-slate-200 rounded-sm bg-white">
          該当する変更申請はありません
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="h-10 px-4 text-left text-xs font-medium text-slate-500 uppercase">申請日時</th>
                <th className="h-10 px-4 text-left text-xs font-medium text-slate-500 uppercase">店舗</th>
                <th className="h-10 px-4 text-left text-xs font-medium text-slate-500 uppercase">配送日</th>
                <th className="h-10 px-4 text-left text-xs font-medium text-slate-500 uppercase">商品</th>
                <th className="h-10 px-4 text-right text-xs font-medium text-slate-500 uppercase">変更内容</th>
                <th className="h-10 px-4 text-left text-xs font-medium text-slate-500 uppercase">理由</th>
                <th className="h-10 px-4 text-center text-xs font-medium text-slate-500 uppercase">状態</th>
                {canApprove && (
                  <th className="h-10 px-4 text-center text-xs font-medium text-slate-500 uppercase w-48">
                    操作
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const product = productsMap[r.product_id] || {};
                const store = storesMap[r.store_id] || {};
                return (
                  <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                    <td className="p-2 px-4 align-middle text-xs text-slate-600">
                      {new Date(r.created_at).toLocaleString("ja-JP")}
                    </td>
                    <td className="p-2 px-4 align-middle text-sm">{store.name || "-"}</td>
                    <td className="p-2 px-4 align-middle text-sm font-mono">{r.delivery_date}</td>
                    <td className="p-2 px-4 align-middle text-sm">
                      <div className="font-medium">{product.name || "-"}</div>
                      <div className="text-xs text-slate-500">{product.code}</div>
                    </td>
                    <td className="p-2 px-4 align-middle text-sm font-mono text-right">
                      {r.old_quantity.toFixed(2)} {r.old_unit || ""} →{" "}
                      <span className="font-semibold text-amber-700">
                        {r.new_quantity.toFixed(2)} {r.new_unit || ""}
                      </span>
                    </td>
                    <td className="p-2 px-4 align-middle text-xs text-slate-600">
                      {r.reason || "-"}
                    </td>
                    <td className="p-2 px-4 align-middle text-center">{statusBadge(r.status)}</td>
                    {canApprove && (
                      <td className="p-2 px-4 align-middle">
                        {r.status === "auto_applied" ? (
                          <Link
                            to={`/admin/print/picking-list?date=${r.delivery_date}`}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`print-${r.id}`}
                            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-sm bg-slate-900 text-white hover:bg-slate-800"
                          >
                            <Printer className="h-3 w-3" />
                            変更後の発注を印刷
                          </Link>
                        ) : r.status === "pending" ? (
                          <div className="flex justify-center gap-1">
                            <Button
                              size="sm"
                              onClick={() => openReview(r, "approve")}
                              data-testid={`approve-${r.id}`}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm h-8"
                            >
                              <Check className="h-3.5 w-3.5 mr-1" />
                              承認
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => openReview(r, "reject")}
                              data-testid={`reject-${r.id}`}
                              variant="outline"
                              className="text-red-600 border-red-300 hover:bg-red-50 rounded-sm h-8"
                            >
                              <X className="h-3.5 w-3.5 mr-1" />
                              却下
                            </Button>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center gap-1">
                            <div className="text-xs text-slate-500 text-center">
                              {r.reviewed_by_name} ({r.review_comment || "-"})
                            </div>
                            {r.status === "approved" && (
                              <Link
                                to={`/admin/print/picking-list?date=${r.delivery_date}`}
                                target="_blank"
                                rel="noreferrer"
                                data-testid={`print-${r.id}`}
                                className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-sm bg-slate-900 text-white hover:bg-slate-800"
                              >
                                <Printer className="h-3 w-3" />
                                変更後の発注を印刷
                              </Link>
                            )}
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog
        open={!!reviewTarget || !!reviewBatchTarget}
        onOpenChange={(o) => {
          if (!o) {
            setReviewTarget(null);
            setReviewBatchTarget(null);
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {reviewBatchTarget
                ? reviewAction === "approve"
                  ? "一括変更申請を承認"
                  : "一括変更申請を却下"
                : reviewAction === "approve"
                ? "変更申請を承認"
                : "変更申請を却下"}
            </DialogTitle>
          </DialogHeader>
          {reviewBatchTarget && (
            <div className="space-y-3 text-sm">
              <div>
                <span className="text-slate-500">店舗: </span>
                <span className="font-medium">
                  {storesMap[reviewBatchTarget.store_id]?.name || "-"}
                </span>
              </div>
              <div>
                <span className="text-slate-500">配送日: </span>
                <span className="font-mono">{reviewBatchTarget.delivery_date}</span>
              </div>
              <div>
                <span className="text-slate-500">申請件数: </span>
                <span className="font-semibold">{(reviewBatchTarget.items || []).length}件</span>
              </div>
              <div>
                <Label htmlFor="comment-batch">コメント（任意）</Label>
                <Input
                  id="comment-batch"
                  data-testid="review-comment"
                  value={reviewComment}
                  onChange={(e) => setReviewComment(e.target.value)}
                  className="mt-1 h-9 rounded-sm"
                />
              </div>
            </div>
          )}
          {reviewTarget && (
            <div className="space-y-3 text-sm">
              <div>
                <span className="text-slate-500">商品: </span>
                <span className="font-medium">
                  {productsMap[reviewTarget.product_id]?.name}
                </span>
              </div>
              <div>
                <span className="text-slate-500">変更: </span>
                <span className="font-mono">
                  {reviewTarget.old_quantity} {reviewTarget.old_unit} →{" "}
                  {reviewTarget.new_quantity} {reviewTarget.new_unit}
                </span>
              </div>
              <div>
                <Label htmlFor="comment">コメント（任意）</Label>
                <Input
                  id="comment"
                  data-testid="review-comment"
                  value={reviewComment}
                  onChange={(e) => setReviewComment(e.target.value)}
                  className="mt-1 h-9 rounded-sm"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setReviewTarget(null);
                setReviewBatchTarget(null);
              }}
              className="rounded-sm"
            >
              キャンセル
            </Button>
            <Button
              onClick={submitReview}
              data-testid="confirm-review"
              className={`rounded-sm text-white ${
                reviewAction === "approve"
                  ? "bg-emerald-600 hover:bg-emerald-700"
                  : "bg-red-600 hover:bg-red-700"
              }`}
            >
              {reviewAction === "approve" ? "承認する" : "却下する"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
